import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, BrainCircuit } from "lucide-react";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, Field } from "./bits";

export function PrivacyControls() {
  const { logout } = useAuth();
  const [s, setS] = useState(null);
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => { api.get("/privacy/settings").then((r) => setS(r.data)); api.get("/privacy/deletion").then((r) => setInfo(r.data)); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggle = async (v) => { try { await api.put("/privacy/settings", { coach_journal_access: v }); setS({ ...s, coach_journal_access: v }); toast.success(v ? "Coach can read your journal" : "Coach will not read your journal"); } catch (e) { toast.error(errText(e)); } };
  const exportData = async () => {
    setBusy("export");
    try {
      const r = await api.get("/privacy/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = (r.headers["content-disposition"] || "").split('filename="')[1]?.replace('"', "") || "expertraq-export.zip"; a.click(); URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const del = async (e) => {
    e.preventDefault(); setBusy("delete");
    try { const { data } = await api.post("/privacy/delete-account", { password: pw }); toast.success(`Deletion scheduled for ${new Date(data.scheduled_at).toLocaleDateString()}. You have been signed out.`); await logout(); }
    catch (ex) { toast.error(errText(ex)); } finally { setBusy(""); }
  };
  const cancel = async () => { await api.post("/privacy/cancel-deletion"); toast.success("Deletion cancelled"); load(); };
  if (!s || !info) return null;
  return (
    <>
      <Card title="AI coach & your journal" testId="coach-journal-card">
        <p className="sub">The AI coach can quote your journal lessons and trade notes to explain patterns. Switch this off and it will only see computed numbers (win rate, risk used, streaks) — never your written words.</p>
        <label className="flex gap-2 items-center" style={{ cursor: "pointer" }}><input type="checkbox" checked={!!s.coach_journal_access} onChange={(e) => toggle(e.target.checked)} className="chk" data-testid="coach-journal-toggle" /> <BrainCircuit size={14} /> Allow the AI coach to read my journal</label>
      </Card>
      <Card title="Export my data" testId="export-data-card">
        <p className="sub">Download a zip with JSON and CSV copies of your accounts, trades, journal, strategies, AI coach history and ledger events.</p>
        <button className="btn" onClick={exportData} disabled={busy === "export"} data-testid="export-data-btn"><Download size={14} /> {busy === "export" ? "Preparing…" : "Download my data"}</button>
      </Card>
      <Card title="Delete my account" testId="delete-account-card">
        {info.pending ? (
          <div className="note bad" data-testid="deletion-pending">Deletion scheduled for <b>{new Date(info.pending.scheduled_at).toLocaleString()}</b>. Sign in any time before then to cancel. <button className="btn sm ghost ml-2" onClick={cancel} data-testid="cancel-deletion-btn">Cancel deletion</button></div>
        ) : (
          <form onSubmit={del}>
            <p className="sub">Your account is signed out immediately and permanently deleted after a <b>{info.grace_days}-day grace period</b>. Signing in during that period cancels the deletion.</p>
            <div className="grid g2">
              <div><div className="rail-k">What is deleted</div><ul className="tight" data-testid="deletion-deleted-list">{info.deleted.map((x) => <li key={x}>{x}</li>)}</ul></div>
              <div><div className="rail-k">What is kept</div><ul className="tight" data-testid="deletion-kept-list">{info.kept.map((x) => <li key={x}>{x}</li>)}</ul></div>
            </div>
            <Field label="Re-enter your password to confirm"><input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="current-password" data-testid="delete-password-input" /></Field>
            <button className="btn" style={{ background: "var(--clay)" }} disabled={busy === "delete" || !pw} data-testid="delete-account-btn"><Trash2 size={14} /> {busy === "delete" ? "…" : "Delete my account"}</button>
          </form>
        )}
      </Card>
    </>
  );
}
